import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext.jsx";
import { useNavigate } from "react-router-dom";
import CardBox from "../components/ui/CardBox.jsx";
import FormField from "../components/form/FormField.jsx";
import FormControl from "../components/form/FormControl.jsx";
import BaseButton from "../components/ui/BaseButton.jsx";
import BaseButtons from "../components/ui/BaseButtons.jsx";
import BaseDivider from "../components/ui/BaseDivider.jsx";
import BaseIcon from "../components/ui/BaseIcon.jsx";
import { mdiRobot } from "@mdi/js";

const LOCK_MESSAGE_TOKEN = "usuario bloqueado temporalmente";

function isLockMessage(message) {
  return typeof message === "string" && message.toLowerCase().includes(LOCK_MESSAGE_TOKEN);
}

function formatLockUntil(lockedUntil) {
  if (!lockedUntil) {
    return null;
  }

  const lockDate = new Date(lockedUntil);
  if (Number.isNaN(lockDate.getTime())) {
    return null;
  }

  return lockDate.toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    dateStyle: "short",
    timeStyle: "short"
  });
}

export default function LoginPage() {
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [lockState, setLockState] = useState({ locked: false, lockedUntil: null });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  function clearAuthFeedback() {
    setError(null);
    setLockState({ locked: false, lockedUntil: null });
  }

  function applyAuthError(message, lockedUntil = null) {
    const finalMessage = typeof message === "string" && message.trim()
      ? message.trim()
      : "Credenciales inválidas";
    const lockDetected = isLockMessage(finalMessage) || Boolean(lockedUntil);
    setError(finalMessage);
    setLockState({
      locked: lockDetected,
      lockedUntil: lockDetected ? lockedUntil : null
    });
  }

  const lockUntilLabel = formatLockUntil(lockState.lockedUntil);
  const fieldError = lockState.locked ? null : error;

  useEffect(() => {
    if (localStorage.getItem("agent_token")) {
      navigate("/", { replace: true });
      return;
    }

    // Retorno desde FluxGuardian OIDC
    const params = new URLSearchParams(window.location.search);
    const oidcToken = params.get("oidc_token");
    const oidcError = params.get("error");

    if (oidcError) {
      applyAuthError(decodeURIComponent(oidcError));
      window.history.replaceState({}, "", "/login");
      return;
    }

    if (oidcToken) {
      localStorage.setItem("agent_token", oidcToken);
      window.history.replaceState({}, "", "/login");
      axios.get("/api/auth/me", { headers: { Authorization: `Bearer ${oidcToken}` } })
        .then(({ data }) => { setUser(data.user); navigate("/", { replace: true }); })
        .catch(() => {
          localStorage.removeItem("agent_token");
          applyAuthError("Error al verificar sesión");
        });
    }
  }, [navigate, setUser]);

  async function handleSubmit(event) {
    event.preventDefault();
    clearAuthFeedback();

    try {
      setLoading(true);
      const { data } = await axios.post("/auth/login", { email, password });
      localStorage.setItem("agent_token", data.token);
      setUser(data.user);
      navigate("/", { replace: true });
    } catch (err) {
      const message = err.response?.data?.message || "Credenciales inválidas";
      const lockedUntil = err.response?.data?.lockedUntil || null;
      applyAuthError(message, lockedUntil);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Logo */}
      <div className="flex flex-col items-center mb-8 gap-3">
        <div className="w-14 h-14 rounded-2xl bg-flux-green flex items-center justify-center shadow-lg">
          <BaseIcon path={mdiRobot} size={28} className="text-white" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">Centro de Agentes Flux</h1>
          <p className="mt-1 text-sm text-slate-400">Ingresá con tu usuario de agente para continuar.</p>
        </div>
      </div>

      <CardBox className="shadow-xl">
        <form onSubmit={handleSubmit} className="p-6 space-y-2">
          {lockState.locked && (
            <div className="login-lock-banner" role="alert" aria-live="polite">
              <p className="login-lock-banner-title">Acceso temporalmente bloqueado</p>
              <p className="login-lock-banner-message">{error}</p>
              {lockUntilLabel && (
                <p className="login-lock-banner-until">Disponible nuevamente: {lockUntilLabel}</p>
              )}
            </div>
          )}

          <FormField label="Correo electrónico">
            <FormControl
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agente@flux.com"
              autoComplete="email"
            />
          </FormField>

          <FormField label="Contraseña" error={fieldError || undefined}>
            <FormControl
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </FormField>

          <div className="pt-2">
            <BaseButton
              type="submit"
              color="success"
              label={loading ? "Ingresando…" : "Ingresar"}
              disabled={loading}
              className="w-full justify-center"
            />
          </div>
        </form>

        <div className="px-6 pb-6">
          <BaseDivider />
          <a
            href="/auth/oidc/login"
            className={[
              "flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5",
              "text-sm font-medium border transition-colors",
              "border-slate-300 dark:border-slate-600",
              "text-gray-700 dark:text-gray-300",
              "hover:bg-gray-50 dark:hover:bg-slate-800",
            ].join(" ")}
          >
            Ingresar con FluxGuardian
          </a>
        </div>
      </CardBox>
    </div>
  );
}
